import { useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

type LetterStatus =
  | 'idle'
  | 'streaming'
  | 'validating'
  | 'validation_failed'
  | 'validation_passed'
  | 'cache_hit'
  | 'error';

interface ModalButton {
  label: string;
  onClick: () => void;
  variant: 'primary' | 'secondary';
}

interface ModalConfig {
  title: string;
  tagline: string | null;
  showSpinner: boolean;
  showContent: boolean;
  contentGrayed: boolean;
  primaryButton: ModalButton | null;
  secondaryButton: ModalButton | null;
}

interface LetterGenerationModalProps {
  isOpen: boolean;
  status: LetterStatus;
  content: string;
  error: string | null;
  onClose: () => void;
  onRetry: () => void;
  onDownload: () => void;
  canClose: boolean;
  isDownloading: boolean;
}

export const LetterGenerationModal = ({
  isOpen,
  status,
  content,
  error,
  onClose,
  onRetry,
  onDownload,
  canClose,
  isDownloading,
}: LetterGenerationModalProps) => {
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && canClose) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [isOpen, canClose, onClose]);

  if (!isOpen || status === 'idle') {
    return null;
  }

  // Modal state configuration
  const getModalConfig = (): ModalConfig => {
    switch (status) {
      case 'streaming':
        return {
          title: 'Writing your letter',
          tagline: 'Analyzing your health profile and generating personalized recommendations.',
          showSpinner: true,
          showContent: true,
          contentGrayed: false,
          primaryButton: null,
          secondaryButton: null,
        };

      case 'validating':
        return {
          title: 'Validating your letter',
          tagline: 'Ensuring accuracy and quality before finalizing your letter.',
          showSpinner: true,
          showContent: false, // Don't show content until validation passes
          contentGrayed: false,
          primaryButton: null,
          secondaryButton: null,
        };

      case 'validation_failed':
        return {
          title: "Let's try that again",
          tagline: null,
          showSpinner: false,
          showContent: true,
          contentGrayed: true,
          primaryButton: {
            label: 'Retry',
            onClick: onRetry,
            variant: 'primary' as const,
          },
          secondaryButton: null,
        };

      case 'validation_passed':
      case 'cache_hit':
        return {
          title: 'Your Letter is Ready',
          tagline: "Everything's backed by peer-reviewed research because your doctor deserves the same quality intel you do.",
          showSpinner: false,
          showContent: true,
          contentGrayed: false,
          primaryButton: {
            label: 'Download PDF',
            onClick: onDownload,
            variant: 'primary' as const,
          },
          secondaryButton: null,
        };

      case 'error':
        return {
          title: 'Something went wrong',
          tagline: null,
          showSpinner: false,
          showContent: false,
          contentGrayed: false,
          primaryButton: {
            label: 'Retry',
            onClick: onRetry,
            variant: 'primary' as const,
          },
          secondaryButton: null,
        };

      default:
        return {
          title: '',
          tagline: null,
          showSpinner: false,
          showContent: false,
          contentGrayed: false,
          primaryButton: null,
          secondaryButton: null,
        };
    }
  };

  const config = getModalConfig();

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={(e) => {
        if (e.target === e.currentTarget && canClose) {
          onClose();
        }
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div className="relative w-full max-w-4xl max-h-[90vh] bg-gradient-to-b from-slate-900 to-slate-800 rounded-2xl shadow-2xl overflow-hidden flex flex-col border border-slate-700">
        {/* Header with icon */}
        <div className="px-8 pt-8 pb-6 flex flex-col items-center text-center">
          {/* Status Icon */}
          <div className="mb-6">
            {config.showSpinner && (
              <div className="w-16 h-16 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
            )}
            {status === 'validation_failed' && (
              <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center">
                <span className="text-3xl text-yellow-400" role="img" aria-label="Warning">
                  ⚠️
                </span>
              </div>
            )}
            {(status === 'validation_passed' || status === 'cache_hit') && (
              <div className="w-16 h-16 bg-teal-600/30 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            )}
            {status === 'error' && (
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
            )}
          </div>

          {/* Title */}
          <h2 id="modal-title" className="text-3xl font-semibold text-white mb-3">
            {config.title}
          </h2>

          {/* Tagline */}
          {config.tagline && (
            <p className="text-slate-400 text-sm max-w-2xl">
              {config.tagline}
            </p>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-8 pb-6">
          {status === 'error' && error && (
            <div className="p-4 bg-red-900/30 border border-red-700 rounded-lg">
              <p className="text-red-200">{error}</p>
            </div>
          )}

          {config.showContent && content && (
            <div className="bg-white rounded-lg p-4 md:p-8 shadow-lg overflow-x-auto">
              <div
                className={`prose prose-slate max-w-none ${
                  config.contentGrayed ? 'opacity-50' : ''
                }`}
                style={{
                  fontSize: '15px',
                  lineHeight: '1.8',
                }}
              >
                <style>{`
                  .prose p {
                    margin-top: 1.25em;
                    margin-bottom: 1.25em;
                  }
                  .prose h1, .prose h2, .prose h3 {
                    margin-top: 2em;
                    margin-bottom: 1em;
                  }
                  .prose h1:first-child, .prose h2:first-child, .prose h3:first-child {
                    margin-top: 0;
                  }
                  .prose ul, .prose ol {
                    margin-top: 1.25em;
                    margin-bottom: 1.25em;
                  }
                  .prose li {
                    margin-top: 0.5em;
                    margin-bottom: 0.5em;
                  }
                  .prose strong {
                    margin-top: 1em;
                    display: inline-block;
                  }
                  .prose table {
                    width: 100%;
                    border-collapse: collapse;
                    margin-top: 1.5em;
                    margin-bottom: 1.5em;
                    min-width: 600px;
                  }
                  .prose th {
                    background-color: #f1f5f9;
                    padding: 0.75rem;
                    text-align: left;
                    font-weight: 600;
                    border: 1px solid #cbd5e1;
                    white-space: nowrap;
                  }
                  .prose td {
                    padding: 0.75rem;
                    border: 1px solid #cbd5e1;
                  }
                  .prose tr:nth-child(even) {
                    background-color: #f8fafc;
                  }
                  @media (max-width: 768px) {
                    .prose table {
                      font-size: 13px;
                    }
                    .prose th,
                    .prose td {
                      padding: 0.5rem;
                    }
                  }
                `}</style>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
              </div>
            </div>
          )}

        </div>

        {/* Footer */}
        {(config.primaryButton || config.secondaryButton) && (
          <div className="px-8 pb-8 pt-4 flex gap-4 justify-center">
            {config.primaryButton && (
              <button
                onClick={config.primaryButton.onClick}
                disabled={isDownloading && config.primaryButton.label === 'Download PDF'}
                className="px-8 py-3 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors font-medium shadow-lg shadow-teal-500/30 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDownloading && config.primaryButton.label === 'Download PDF' ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Generating PDF...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    {config.primaryButton.label}
                  </>
                )}
              </button>
            )}
            {config.secondaryButton && (
              <button
                onClick={config.secondaryButton.onClick}
                className="px-8 py-3 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 transition-colors font-medium flex items-center gap-2"
              >
                {config.secondaryButton.label === 'Email to doctor' && (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                )}
                {config.secondaryButton.label}
              </button>
            )}
          </div>
        )}

        {/* Close button */}
        {canClose && status !== 'streaming' && status !== 'validating' && (
          <button
            onClick={onClose}
            className="mt-4 mb-6 mx-auto block text-slate-400 hover:text-slate-200 transition-colors text-sm"
          >
            Close
          </button>
        )}
      </div>
    </div>
  );
};
